import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Title, Text, Image, Group, Badge, Divider, Button, Stack, Paper, Anchor, Avatar, Box } from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { blogPosts, BlogPostData } from './blogData';
import BlogPostSEO from './BlogPostSEO';

export function BlogPost() {
  const { id } = useParams<{ id: string }>();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  
  const post = blogPosts.find((post: BlogPostData) => post.id === id);
  
  useEffect(() => {
    if (post) {
      // In a real app, you would fetch the markdown file
      // For now, we'll simulate loading the content
      fetch(post.contentUrl)
        .then(response => response.text())
        .then(text => {
          setContent(text);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading markdown:', error);
          setLoading(false);
        });
    }
  }, [post]);
  
  if (!post) {
    return (
      <Container size="md" py="xl">
        <Title order={2}>Post not found</Title>
        <Button component={Link} to="/blog" mt="md">
          Back to Blog
        </Button>
      </Container>
    );
  }
  
  // Find next and previous posts for navigation
  const currentIndex = blogPosts.findIndex((p: BlogPostData) => p.id === id);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;
  
  return (
    <>
      <BlogPostSEO
        title={post.title}
        description={post.description}
        image={post.imageUrl}
        datePublished={post.date}
        author={post.author.name}
        url={window.location.origin + '/blog/' + post.id}
      />
      <Container size="md" py="xl" style={{ marginTop: '80px' }}>
        <Button component={Link} to="/blog" mb="md" variant="subtle">
          ← Back to Blog
        </Button>
        
        <Paper shadow="xs" p="md" withBorder>
          <Image
            src={post.imageUrl}
            height={300}
            alt={post.title}
            radius="md"
          />
          
          <Title order={1} mt="lg">{post.title}</Title>

          <Text c="dimmed">Date Published: {post.date}</Text>

          {/* todo: do we want to include author info/contact? */}
          {/*<Group mb="lg" p="md" style={{ background: '#f8f9fa', borderRadius: '8px' }}>*/}
          {/*  <Avatar src={post.author.photoUrl} radius="xl" size="lg" />*/}
          {/*  <Box>*/}
          {/*    <Text size="md" fw={500}>{post.author.name}</Text>*/}
          {/*    <Anchor */}
          {/*      href={`mailto:${post.author.email}`} */}
          {/*      size="sm" */}
          {/*      c="dimmed"*/}
          {/*      onClick={(e) => e.stopPropagation()}*/}
          {/*    >*/}
          {/*      {post.author.email}*/}
          {/*    </Anchor>*/}
          {/*  </Box>*/}
          {/*</Group>*/}
          
          <Divider my="lg" />
          
          {loading ? (
            <Text>Loading content...</Text>
          ) : (
            <div className="blog-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
            </div>
          )}

          {/*todo: add verticle spacing here mantine 'md' */}

          {post.tags?.map(tag => (
              <Badge key={tag} color="blue" variant="light">{tag}</Badge>
          ))}

          <Divider my="lg" />
          
          <Stack mt="xl">
            <Group justify="space-between">
              {prevPost ? (
                <Anchor component={Link} to={`/blog/${prevPost.id}`}>
                  ← {prevPost.title}
                </Anchor>
              ) : (
                <div></div>
              )}
              
              {nextPost && (
                <Anchor component={Link} to={`/blog/${nextPost.id}`}>
                  {nextPost.title} →
                </Anchor>
              )}
            </Group>
          </Stack>
        </Paper>
      </Container>
    </>
  );
}
